/**
 * LedgerOverview - Premium medical expense ledger
 *
 * Design Philosophy:
 * - Ledger front and center (primary view)
 * - Liens as secondary tab
 * - Inline creation and editing
 * - Minimalist, focused, whitespace-first
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLedgerToast } from '@/hooks/useLedgerToast';
import {
  BenefitUtilization,
  CreateAllocation,
  ReadExpense,
  ReadLien,
  useGetLedgerSummaryQuery,
  useLienListQuery,
  useReplaceExpenseAllocationsMutation,
} from '@/services/api/ledgerService';

import { DomainEventsFeed } from '../domain-events-v2/DomainEventsFeed';
import { FinchEventCode } from '../domain-events-v2/types';
import { AllocationEditor } from './expenses/AllocationEditor';
import { ExpenseFormDialog } from './expenses/ExpenseFormDialog';
import { ExpensesTable } from './expenses/ExpensesTable';
import { LienFormDialog } from './liens/LienFormDialog';
import { LiensTable } from './liens/LiensTable';
import { ProviderBreakdown } from './providers/ProviderBreakdown';
import { formatCurrency, parseDateOnly } from './shared/formatUtils';

/** Expense-related event codes for audit log filtering */
const EXPENSE_EVENT_CODES: FinchEventCode[] = [
  'expense_created',
  'expense_updated',
  'expense_deleted',
  'allocations_replaced',
  'lien_created',
  'lien_updated',
  'lien_deleted',
];

/** Extract the primary limit from a benefit's limitations, returning key and value */
const getBenefitLimitInfo = (
  benefit: BenefitUtilization | null,
): { key: string; value: number } | null => {
  if (!benefit?.limitations) return null;
  // Check common limitation keys in priority order
  const keys = ['per person', 'per accident', 'Combined single limit'];
  const found = keys
    .map((key) => ({ key, value: benefit.limitations?.[key] }))
    .find((item) => item.value != null);
  if (found) return { key: found.key, value: found.value as number };
  // Fallback: return first available limit
  const entries = Object.entries(benefit.limitations);
  if (entries.length > 0) {
    const [key, value] = entries[0];
    return { key, value };
  }
  return null;
};

type DialogState =
  | { type: 'closed' }
  | { type: 'expense'; expense?: ReadExpense }
  | { type: 'allocations'; expense: ReadExpense }
  | { type: 'lien'; lien?: ReadLien };

export const LedgerOverview = () => {
  const { matter_id } = useParams<{ matter_id: string }>();
  const { showSuccess, showError } = useLedgerToast();

  const [dialog, setDialog] = useState<DialogState>({ type: 'closed' });
  const [allocations, setAllocations] = useState<CreateAllocation[]>([]);
  const [replaceAllocations, { isLoading: isSavingAllocations }] =
    useReplaceExpenseAllocationsMutation();
  const [providerFilter, setProviderFilter] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState('ledger');

  const { data: summary, isLoading: isSummaryLoading } =
    useGetLedgerSummaryQuery(
      { matterId: matter_id ?? '' },
      { skip: !matter_id },
    );
  const { data: liens } = useLienListQuery(
    { matterId: matter_id ?? '' },
    { skip: !matter_id },
  );

  if (!matter_id) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-600 font-medium">Matter ID not found</p>
      </div>
    );
  }

  const openExpenseDialog = (expense?: ReadExpense) =>
    setDialog({ type: 'expense', expense });

  const openAllocationsDialog = (expense: ReadExpense) => {
    setAllocations(
      expense.allocations.map((a) => ({
        allocation_bucket: a.allocation_bucket,
        payer_type: a.payer_type,
        payer_id: a.payer_provider?.id ?? a.payer_coverage?.id ?? null,
        amount: a.amount,
      })),
    );
    setDialog({ type: 'allocations', expense });
  };

  const openLienDialog = (lien?: ReadLien) => setDialog({ type: 'lien', lien });
  const closeDialog = () => setDialog({ type: 'closed' });

  const handleSaveAllocations = async () => {
    if (dialog.type !== 'allocations') return;
    try {
      await replaceAllocations({
        matterId: matter_id,
        id: dialog.expense.id,
        createAllocationList: allocations,
      }).unwrap();
      showSuccess('update', 'allocation');
      closeDialog();
    } catch {
      showError();
    }
  };

  const handleProviderClick = (providerId: string) => {
    setProviderFilter(providerId);
    setActiveTab('ledger');
  };

  const pipLimitInfo = getBenefitLimitInfo(summary?.pip ?? null);
  const medpayLimitInfo = getBenefitLimitInfo(summary?.medpay ?? null);
  const thirdPartyBiLimitInfo = getBenefitLimitInfo(
    summary?.third_party_bi ?? null,
  );
  const totalBilled = summary ? parseFloat(summary.total_billed) : 0;
  const lienExposure = summary
    ? parseFloat(summary.lien_exposure?.total ?? '0')
    : 0;

  // Bucket totals
  const buckets = summary?.by_bucket;
  const clientPaid = buckets ? parseFloat(buckets.client ?? '0') : 0;
  const pipPaid = buckets ? parseFloat(buckets.pip ?? '0') : 0;
  const medpayPaid = buckets ? parseFloat(buckets.medpay ?? '0') : 0;
  const healthInsurancePaid = buckets
    ? parseFloat(buckets.health_insurance ?? '0')
    : 0;
  const unallocated = buckets ? parseFloat(buckets.unallocated ?? '0') : 0;

  return (
    <div className="bg-white">
      <div className="max-w-[1400px] mx-auto px-6 py-3">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Compact header: tabs left, stats right */}
          <div className="flex items-center justify-between gap-4 mb-3 pb-3 border-b border-slate-200">
            <TabsList className="bg-transparent h-auto p-0 gap-1">
              <TabsTrigger
                value="ledger"
                className="data-[state=active]:bg-slate-100 data-[state=active]:shadow-none rounded px-2.5 py-1 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 font-medium"
              >
                Expenses
              </TabsTrigger>
              <TabsTrigger
                value="providers"
                className="data-[state=active]:bg-slate-100 data-[state=active]:shadow-none rounded px-2.5 py-1 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 font-medium"
              >
                Providers
              </TabsTrigger>
              <TabsTrigger
                value="liens"
                className="data-[state=active]:bg-slate-100 data-[state=active]:shadow-none rounded px-2.5 py-1 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 font-medium"
              >
                Liens
              </TabsTrigger>
              <TabsTrigger
                value="audit"
                className="data-[state=active]:bg-slate-100 data-[state=active]:shadow-none rounded px-2.5 py-1 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 font-medium"
              >
                Audit Log
              </TabsTrigger>
            </TabsList>

            {/* Stats with popover breakdown */}
            {!isSummaryLoading && summary && (
              <div className="flex items-center gap-4 text-sm">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="font-semibold text-slate-900 underline decoration-dotted underline-offset-4 hover:text-slate-700 cursor-help"
                    >
                      {formatCurrency(totalBilled)} billed
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-3" align="start">
                    <div className="space-y-1.5 text-sm">
                      <p className="font-medium text-slate-900 pb-1 border-b">
                        Payment Breakdown
                      </p>
                      {clientPaid > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Client</span>
                          <span className="font-medium">
                            {formatCurrency(clientPaid)}
                          </span>
                        </div>
                      )}
                      {pipPaid > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">PIP</span>
                          <span className="font-medium">
                            {formatCurrency(pipPaid)}
                          </span>
                        </div>
                      )}
                      {medpayPaid > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">MedPay</span>
                          <span className="font-medium">
                            {formatCurrency(medpayPaid)}
                          </span>
                        </div>
                      )}
                      {healthInsurancePaid > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Health Ins</span>
                          <span className="font-medium">
                            {formatCurrency(healthInsurancePaid)}
                          </span>
                        </div>
                      )}
                      {unallocated > 0 && (
                        <div className="flex justify-between">
                          <span className="text-amber-600">Unknown</span>
                          <span className="font-medium text-amber-600">
                            {formatCurrency(unallocated)}
                          </span>
                        </div>
                      )}
                      {lienExposure > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Provider Liens</span>
                          <span className="font-medium">
                            {formatCurrency(lienExposure)}
                          </span>
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                {unallocated > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-amber-600 text-xs cursor-help underline decoration-dotted">
                        {formatCurrency(unallocated)} unknown payer
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-sm">
                        This amount hasn&apos;t been assigned to a specific
                        insurance or payer yet. Click an expense to track who
                        paid for it.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            )}
          </div>

          {/* Coverage utilization mini-bars */}
          {!isSummaryLoading &&
            summary &&
            (thirdPartyBiLimitInfo || pipLimitInfo || medpayLimitInfo) && (
              <div className="flex items-center gap-6 mb-3 text-sm">
                {thirdPartyBiLimitInfo && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600">
                      3P BI
                      {summary.third_party_bi?.carrier_name
                        ? ` (${summary.third_party_bi.carrier_name})`
                        : ''}
                      :
                    </span>
                    <span className="font-medium">
                      {formatCurrency(totalBilled)}/
                      {formatCurrency(thirdPartyBiLimitInfo.value)}
                    </span>
                    <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={(() => {
                          const pct = totalBilled / thirdPartyBiLimitInfo.value;
                          if (pct >= 1) return 'h-full rounded-full bg-red-500';
                          if (pct >= 0.8)
                            return 'h-full rounded-full bg-amber-500';
                          return 'h-full rounded-full bg-emerald-500';
                        })()}
                        style={{
                          width: `${Math.min((totalBilled / thirdPartyBiLimitInfo.value) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
                {pipLimitInfo && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600">
                      PIP
                      {summary.pip?.carrier_name
                        ? ` (${summary.pip.carrier_name})`
                        : ''}
                      :
                    </span>
                    <span className="font-medium">
                      {formatCurrency(pipPaid)}/
                      {formatCurrency(pipLimitInfo.value)}
                    </span>
                    <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={(() => {
                          const pct = pipPaid / pipLimitInfo.value;
                          if (pct >= 1) return 'h-full rounded-full bg-red-500';
                          if (pct >= 0.8)
                            return 'h-full rounded-full bg-amber-500';
                          return 'h-full rounded-full bg-emerald-500';
                        })()}
                        style={{
                          width: `${Math.min((pipPaid / pipLimitInfo.value) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
                {medpayLimitInfo && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600">
                      MedPay
                      {summary.medpay?.carrier_name
                        ? ` (${summary.medpay.carrier_name})`
                        : ''}
                      :
                    </span>
                    <span className="font-medium">
                      {formatCurrency(medpayPaid)}/
                      {formatCurrency(medpayLimitInfo.value)}
                    </span>
                    <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={(() => {
                          const pct = medpayPaid / medpayLimitInfo.value;
                          if (pct >= 1) return 'h-full rounded-full bg-red-500';
                          if (pct >= 0.8)
                            return 'h-full rounded-full bg-amber-500';
                          return 'h-full rounded-full bg-emerald-500';
                        })()}
                        style={{
                          width: `${Math.min((medpayPaid / medpayLimitInfo.value) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

          <TabsContent
            value="ledger"
            className="mt-0 focus-visible:outline-none"
          >
            <ExpensesTable
              matterId={matter_id}
              providerId={providerFilter}
              onAddExpense={() => openExpenseDialog()}
              onEditExpense={openExpenseDialog}
              onEditAllocations={openAllocationsDialog}
            />
          </TabsContent>

          <TabsContent
            value="providers"
            className="mt-0 focus-visible:outline-none"
          >
            <ProviderBreakdown
              matterId={matter_id}
              onProviderClick={handleProviderClick}
            />
          </TabsContent>

          <TabsContent
            value="liens"
            className="mt-0 focus-visible:outline-none"
          >
            <LiensTable
              matterId={matter_id}
              onEditLien={openLienDialog}
              onAddLien={() => openLienDialog()}
            />
          </TabsContent>


          <TabsContent
            value="audit"
            className="mt-0 focus-visible:outline-none"
          >
            <div className="bg-white rounded-lg border border-slate-200 h-[600px]">
              <DomainEventsFeed
                matterId={matter_id}
                eventCodes={EXPENSE_EVENT_CODES}
                limit={100}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <ExpenseFormDialog
        matterId={matter_id}
        expense={dialog.type === 'expense' ? dialog.expense : undefined}
        open={dialog.type === 'expense'}
        onOpenChange={(open) => !open && closeDialog()}
      />

      <LienFormDialog
        matterId={matter_id}
        lien={dialog.type === 'lien' ? dialog.lien : undefined}
        open={dialog.type === 'lien'}
        onOpenChange={(open) => !open && closeDialog()}
      />


      {/* Allocations Editor Dialog */}
      <Dialog
        open={dialog.type === 'allocations'}
        onOpenChange={(open) => !open && closeDialog()}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b border-slate-100">
            <DialogTitle className="text-lg">Who Paid?</DialogTitle>
            <DialogDescription className="text-sm">
              Track which insurance or payer covered this medical charge
            </DialogDescription>
          </DialogHeader>

          {dialog.type === 'allocations' && (
            <div className="flex flex-col">
              <div className="px-6 py-5 space-y-5">
                <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {dialog.expense.provider.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {dialog.expense.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-slate-900">
                      {formatCurrency(parseFloat(dialog.expense.billed_amount))}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {parseDateOnly(dialog.expense.service_date).toLocaleDateString(
                        'en-US',
                        {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        },
                      )}
                    </p>
                  </div>
                </div>

                <AllocationEditor
                  allocations={allocations}
                  billedAmount={dialog.expense.billed_amount}
                  onChange={setAllocations}
                  matterId={matter_id}
                  expenseProviderId={dialog.expense.provider_id as string}
                  expenseProviderName={dialog.expense.provider.name}
                  providerHasLien={
                    liens?.some(
                      (lien) =>
                        lien.lienholder_type === 'PROVIDER' &&
                        lien.lienholder_provider?.id ===
                          dialog.expense.provider_id &&
                        lien.status === 'ACTIVE',
                    ) ?? false
                  }
                />
              </div>

              <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeDialog}
                  disabled={isSavingAllocations}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleSaveAllocations}
                  disabled={isSavingAllocations}
                >
                  {isSavingAllocations ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
